// Pure geometry for the Risk → Live vs backtest edge map (spec 2026-10-30 §6).
// Inputs are bp/day from /api/risk/edge-map; the axes are % per year (bp/day × ANNUAL).
import type { EdgeMapFund, EdgePoint } from '../../api/client'

export const ANNUAL = 2.52
export const PLOT = { x0: 80, x1: 740, y0: 30, y1: 380, width: 760, height: 440 } as const
export const BOUNDARY_PCT = 10
export const STRONG_PCT = 50

export type Horizon = '1w' | '1m' | '3m' | '6m' | '1y' | '2y' | '3y'
export const HORIZONS: readonly Horizon[] = ['1w', '1m', '3m', '6m', '1y', '2y', '3y']

export type Scope = 'Live' | 'Candidates' | 'All'
export type Quadrant = 'staleEdge' | 'staleTrend' | 'noEdge' | 'trendPaid'

/** What the axes measure: 'return' = % per year; 'risk' = return per unit of risk (annualised return / annualised
 *  NAV volatility over the same window, a Sharpe-style ratio). */
export type Measure = 'return' | 'risk'
/** What the bubble size shows: the rule's total payoff, or its compounded annual return (CAGR). */
export type Circle = 'payoff' | 'cagr'
export const MEASURES: readonly Measure[] = ['return', 'risk']
export const CIRCLES: readonly Circle[] = ['payoff', 'cagr']

/** Per-measure axis rules. `factor` turns a point's value into axis units (bp/day to % per year for 'return'; risk
 *  points are already ratios). Boundary and strong lines, minimum ranges and rounding are in axis units. */
export interface AxisSpec {
  factor: number; minCx: [number, number]; minSy: [number, number]; round: number
  boundary: number; strong: number
}
export const AXES: Record<Measure, AxisSpec> = {
  return: { factor: ANNUAL, minCx: [-20, 45], minSy: [0, 175], round: 5, boundary: BOUNDARY_PCT, strong: STRONG_PCT },
  // 0.3 ~ 10 % a year at a typical 30 % volatility; 1.5 ~ the 50 %-a-year "strong" line at the same volatility.
  risk: { factor: 1, minCx: [-0.5, 1], minSy: [0, 3], round: 0.25, boundary: 0.3, strong: 1.5 },
}

/** Axis ranges in axis units (see {@link AxisSpec}): cx = continuation (horizontal), sy = stale-pricing (vertical). */
export interface Scale { cx: [number, number]; sy: [number, number]; factor: number }

const PAD = 0.06

function padded(values: number[], min: [number, number], round: number): [number, number] {
  const lo = Math.min(min[0], ...values)
  const hi = Math.max(min[1], ...values)
  const pad = (hi - lo) * PAD
  return [Math.floor((lo - pad) / round) * round, Math.ceil((hi + pad) / round) * round]
}

/** Autoscale to the points in view, never narrower than the default picture, padded 6 % and rounded. */
export function scaleFor(points: { continuationBp: number; staleBp: number }[], spec: AxisSpec = AXES.return): Scale {
  return {
    cx: padded(points.map(p => p.continuationBp * spec.factor), spec.minCx, spec.round),
    sy: padded(points.map(p => p.staleBp * spec.factor), spec.minSy, spec.round),
    factor: spec.factor,
  }
}

export function px(scale: Scale, continuationBp: number): number {
  const [lo, hi] = scale.cx
  return PLOT.x0 + (continuationBp * scale.factor - lo) / (hi - lo) * (PLOT.x1 - PLOT.x0)
}

export function py(scale: Scale, staleBp: number): number {
  const [lo, hi] = scale.sy
  return PLOT.y1 - (staleBp * scale.factor - lo) / (hi - lo) * (PLOT.y1 - PLOT.y0)
}

const SQRT_YEAR = Math.sqrt(252)

/** NAV volatility, % a year, from the window's bp/day standard deviation; null when not stored yet. */
export function volPctYear(p: EdgePoint): number | null {
  return p.volBp != null && p.volBp > 0 ? p.volBp * SQRT_YEAR / 100 : null
}

/** The point as drawn. 'return' passes it through. 'risk' divides stale, continuation and payoff by the window's
 *  volatility and annualises (bp/day / bp/day x sqrt(252) = annual return / annual volatility); null when the point
 *  has no volatility (rows stored before V159, until the backfill rewrites them). */
export function viewPoint(p: EdgePoint, measure: Measure): EdgePoint | null {
  if (measure === 'return') return p
  if (p.volBp == null || !(p.volBp > 0)) return null
  const k = SQRT_YEAR / p.volBp
  return { ...p, staleBp: p.staleBp * k, continuationBp: p.continuationBp * k, payoffBp: p.payoffBp * k }
}

// Radius per unit of size value: payoff in bp/day keeps the original 0.3; a risk-mode payoff ratio uses 6 per unit
// (a ratio of 3 draws like ~60 bp/day); CAGR is drawn on its log growth, 100 ln(1 + CAGR), at the bp/day scale's
// 0.3 / ANNUAL per % a year, so a CAGR circle and a payoff circle line up and the gap is the volatility drag.
const RISK_PAYOFF_PER_UNIT = 6
const CAGR_PER_LOG_PCT = 0.3 / ANNUAL

/** Bubble radius for the chosen circle, from the point as drawn ({@link viewPoint}); floors at 6. */
export function bubbleRadius(view: EdgePoint, measure: Measure, circle: Circle): number {
  if (circle === 'cagr') {
    const c = view.ruleCagrPct
    if (c == null || !(c > -100)) return 6
    return Math.max(6, 6 + Math.max(0, 100 * Math.log(1 + c / 100)) * CAGR_PER_LOG_PCT)
  }
  if (measure === 'risk') return Math.max(6, 6 + Math.max(0, view.payoffBp) * RISK_PAYOFF_PER_UNIT)
  return radius(view.payoffBp)
}

/** Bubble radius from the rule payoff (bp/day); a zero or negative payoff still gets a clickable bubble. */
export function radius(payoffBp: number): number {
  return Math.max(6, 6 + Math.max(0, payoffBp) * 0.3)
}

export function ticks(lo: number, hi: number): number[] {
  const span = hi - lo
  const step = span > 300 ? 100 : span > 150 ? 50 : span > 60 ? 25 : span > 15 ? 10
    : span > 6 ? 1 : span > 2.5 ? 0.5 : 0.25
  const out: number[] = []
  // integer steps from the first tick, so fractional steps never accumulate float error
  const first = Math.ceil(lo / step - 1e-9)
  for (let i = first; i * step <= hi + 1e-9; i++) out.push(Math.round(i * step * 1000) / 1000 || 0)
  return out
}

/** Quadrant of a drawn point against the measure's boundary lines (upper/right side owns the line). */
export function quadrantIn(p: { continuationBp: number; staleBp: number }, spec: AxisSpec): Quadrant {
  const stale = p.staleBp * spec.factor >= spec.boundary
  const trend = p.continuationBp * spec.factor >= spec.boundary
  if (stale) return trend ? 'staleTrend' : 'staleEdge'
  return trend ? 'trendPaid' : 'noEdge'
}

/** Quadrant at the 10 %-a-year lines on both axes; a point exactly on a line belongs to the upper/right side. */
export function quadrant(p: { continuationBp: number; staleBp: number }): Quadrant {
  const stale = p.staleBp * ANNUAL >= BOUNDARY_PCT
  const trend = p.continuationBp * ANNUAL >= BOUNDARY_PCT
  if (stale) return trend ? 'staleTrend' : 'staleEdge'
  return trend ? 'trendPaid' : 'noEdge'
}

/** The fund's route from the chosen horizon to now: [ghost, …each shorter horizon that exists…, now].
 *  Empty when the fund has no point at the chosen horizon (history too short). */
export function pathThrough(fund: EdgeMapFund, horizon: Horizon): EdgePoint[] {
  const ghost = fund.horizons[horizon]
  if (!ghost) return []
  const shorter = HORIZONS.slice(0, HORIZONS.indexOf(horizon)).slice().reverse()
    .map(h => fund.horizons[h])
    .filter((p): p is EdgePoint => !!p)
  return [ghost, ...shorter, fund.now]
}

export function inScope(fund: EdgeMapFund, scope: Scope): boolean {
  if (scope === 'All') return true
  return scope === 'Live' ? fund.status === 'live' : fund.status === 'candidate'
}
